import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { SignInScreen } from "../screens/SignInScreen";
import { OtpScreen } from "../screens/OtpScreen";
import { PasswordScreen } from "../screens/PasswordScreen";
import { CreatePasswordScreen } from "../screens/CreatePasswordScreen";
import { SetPasswordScreen } from "../screens/SetPasswordScreen";
import { PhoneSignInScreen } from "../screens/PhoneSignInScreen";
import { PhoneOtpScreen } from "../screens/PhoneOtpScreen";
import { SuccessScreen } from "../screens/SuccessScreen";
import { OnboardingFlow } from "../screens/OnboardingFlow";
import type { OnboardingAnswers } from "../screens/OnboardingFlow";
import { IntroFlow } from "../screens/IntroFlow";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";
import { PHONE_LOGIN_ENABLED } from "../lib/featureFlags";

/**
 * Consume the Google-OAuth return fragment if one is present in the URL.
 *
 * The Flask callback (server.py /api/auth/google/callback) redirects the
 * browser to /?google_auth=ok#bsure_token=…&bsure_user=…&bsure_new=…
 * (fragment, NOT query, so the token never hits any server log).
 *
 * On a fresh /login mount we look for that fragment, hydrate localStorage
 * exactly the way OTP login does (bsure_token + ig_t — chat/realApi.ts and
 * the legacy form.html read different keys, so we set both), then clean
 * the URL and return a routing decision: "new" → onboarding, "returning" → /chat.
 *
 * Returns null if no fragment was found (normal page load).
 */
type OAuthReturn = { isNew: boolean; email: string } | null;
function consumeGoogleOAuthReturn(): OAuthReturn {
  if (typeof window === "undefined") return null;
  try {
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) return null;
    const params = new URLSearchParams(hash);
    const token = params.get("bsure_token");
    const userJson = params.get("bsure_user");
    const isNew = params.get("bsure_new") === "1";
    if (!token || !userJson) return null;

    let user: { id?: string; email?: string; name?: string } | null = null;
    try {
      user = JSON.parse(userJson);
    } catch {
      user = null;
    }
    if (!user || !user.id) return null;

    // Hydrate both storage conventions so the React app (bsure_token) and
    // the legacy form.html (ig_t / ig_u) both see the session immediately.
    window.localStorage.setItem("bsure_token", token);
    window.localStorage.setItem("ig_t", token);
    window.localStorage.setItem("ig_u", JSON.stringify(user));
    if (user.email) {
      window.localStorage.setItem("bsure_email", user.email);
    }

    // Clean the URL so a refresh doesn't replay the login (and the token
    // doesn't sit in the browser history bar).
    try {
      const cleanSearch = (window.location.search || "")
        .replace(/[?&]google_auth=[^&]*/, "")
        .replace(/^&/, "?");
      const cleanUrl =
        window.location.pathname + cleanSearch.replace(/[?&]$/, "");
      window.history.replaceState({}, "", cleanUrl);
    } catch {
      // ignore — non-fatal
    }

    return { isNew, email: user.email || "" };
  } catch {
    return null;
  }
}

/**
 * If the callback redirected back with ?auth_error=<code>, map the code to
 * a human-readable message and return it. The caller surfaces this above
 * the sign-in form so the user understands why the Google flow failed.
 */
function consumeOAuthError(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const m = (window.location.search || "").match(/[?&]auth_error=([^&]+)/);
    if (!m) return null;
    const code = decodeURIComponent(m[1] || "");
    const messages: Record<string, string> = {
      callback_failed: "Sign-in could not complete. Please try again or use email.",
      auth_failed: "Sign-in could not be verified. Please try again.",
      missing_params: "Sign-in was interrupted. Please try again.",
      no_email: "Google did not share an email address. Please use email sign-in.",
      disposable_email:
        "Please use a real email address (Gmail, Outlook, work email).",
      oauth_unavailable:
        "Google sign-in is temporarily unavailable. Please use email.",
      access_denied: "Sign-in cancelled.",
    };
    // Clean the URL so a refresh doesn't show the error again.
    try {
      const cleanSearch = (window.location.search || "")
        .replace(/[?&]auth_error=[^&]*/, "")
        .replace(/^&/, "?");
      const cleanUrl =
        window.location.pathname + cleanSearch.replace(/[?&]$/, "");
      window.history.replaceState({}, "", cleanUrl);
    } catch {
      // ignore
    }
    return messages[code] || `Sign-in failed: ${code}`;
  } catch {
    return null;
  }
}

/**
 * Beyond Sure — /login route.
 *
 * Hosts the full pre-chat experience (signin → otp → success → onboarding →
 * intro). The internal state-machine that used to live in App.tsx now lives
 * HERE so /login is self-contained, and the other routes (/chat, /, future
 * marketing landing) stay clean.
 *
 * The screen DESIGNS themselves are NOT touched — this file only mounts
 * them and threads navigation. When the intro flow completes the user is
 * pushed to /chat via react-router-dom's navigate().
 *
 * Dev hook: callers (App.tsx) can pass `initialScreen` to jump directly to
 * any step on mount, e.g. for the dev menu. Production wires nothing.
 */

export type LoginScreen =
  | "signin"
  | "otp"
  | "password"        // BD1 — returning account with a password
  | "create-password" // BD1 — new email chooses a password pre-OTP
  | "set-password"    // BD1 — forced backfill / post-forgot reset
  | "phone-signin"  // gated on PHONE_LOGIN_ENABLED
  | "phone-otp"     // gated on PHONE_LOGIN_ENABLED
  | "success"
  | "onboarding"
  | "intro";

export const LOGIN_SCREENS: LoginScreen[] = [
  "signin",
  "otp",
  "password",
  "create-password",
  "set-password",
  "phone-signin",
  "phone-otp",
  "success",
  "onboarding",
  "intro",
];

// Fake answers used by the dev switcher when jumping into intro without
// having walked through onboarding — so the tailoring logic has something
// realistic to react to. Exported so /chat can reuse the same shape.
export const DEV_FAKE_ANSWERS: OnboardingAnswers = {
  // v6.2 — multi-select. Picking Hindi + English IS Hinglish.
  languages: ["hi", "en"],
  // v6.2 — persona is "customer" by default in the dev fake so the long
  // 16-step IntroFlow + chatbot tailoring exercise the realistic case.
  // Switch to "agent" / "agency" / "bfsi" here to dev-test the lean path.
  persona: "customer",
  businessName: "",
  businessType: null,
  teamSize: null,
  focusArea: [],
  name: "Shivansh",
  age: "32",
  pincode: "560001",
  dependents: { self: true, spouse: true, children: 1, parents: 2 },
  employment: "salaried",
  employerCover: "yes",
  income: "12-25L",
  budget: "25-50k",
  existing: ["health", "motor"],
  liabilities: ["home-loan"],
  goal: "health-cover",
  parentsAge: "60-70",
  pastClaim: { skipped: false, answer: "no" },
  contact: { channel: "whatsapp", time: "evening" },
  decisionStage: "planning",
  health: { skipped: false, smoker: false, preExisting: ["diabetes"] },
  policyUpload: { skipped: false, fileName: "tata-aig-policy-2024.pdf" },
  subscribe: true,
};

interface LoginRouteProps {
  /** Dev-only: jump directly to this screen on mount. */
  initialScreen?: LoginScreen;
  /** Dev-only: prime the auth state so OTP/success aren't blank. */
  initialEmail?: string;
}

export function LoginRoute({
  initialScreen = "signin",
  initialEmail = "",
}: LoginRouteProps) {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<LoginScreen>(initialScreen);
  const [email, setEmail] = useState(initialEmail);
  // Normalized 10-digit Indian mobile, populated by PhoneSignInScreen so
  // PhoneOtpScreen has it for the verify call. Empty until the user opts
  // into the phone flow (which is itself gated on PHONE_LOGIN_ENABLED).
  const [phone, setPhone] = useState<string>("");
  const [answers, setAnswers] = useState<OnboardingAnswers | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  // BD1 — email-first auth state. signupPassword holds the password chosen
  // on CreatePasswordScreen for exactly one hop (create-password → otp);
  // never persisted, never logged. backfillForced marks a KNOWN passwordless
  // existing account (check-email said so) → forced set-password after OTP.
  // wantsReset marks a "Forgot password?" OTP round-trip → optional reset.
  const [signupPassword, setSignupPassword] = useState<string>("");
  const [backfillForced, setBackfillForced] = useState(false);
  const [wantsReset, setWantsReset] = useState(false);

  // Google-OAuth return handler. Runs ONCE on mount, before any screen
  // renders, so a successful Google round-trip lands the user directly in
  // /chat (returning user) or in onboarding (new user) — they never see
  // the sign-in form flash. If the callback returned an error instead, we
  // capture the human-readable message and surface it above the form.
  //
  // This is the bug the founder reported: without this handler, the
  // /api/auth/google/callback redirect to /?google_auth=ok#bsure_token=…
  // would mount the SignInScreen again and the fragment would be
  // ignored — looking exactly like "Google did nothing".
  useEffect(() => {
    const oauthReturn = consumeGoogleOAuthReturn();
    if (oauthReturn) {
      if (oauthReturn.email) setEmail(oauthReturn.email);
      if (oauthReturn.isNew) {
        // New user — pre-fill email, skip OTP (already authenticated by
        // Google), drop them into onboarding so they pick languages/persona.
        setScreen("onboarding");
      } else {
        // Returning user — straight to chat.
        navigate("/chat", { replace: true });
      }
      return;
    }
    const errMsg = consumeOAuthError();
    if (errMsg) setOauthError(errMsg);
    // Intentionally one-shot — no deps, runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the dev switcher jumped us into the intro without onboarding, seed
  // fake answers so the tailoring has something realistic to react to.
  useEffect(() => {
    if (screen === "intro" && !answers) {
      setAnswers(DEV_FAKE_ANSWERS);
    }
    if ((screen === "otp" || screen === "success") && !email) {
      setEmail("dev@beyondsure.in");
    }
  }, [screen, answers, email]);

  // Shared screen transition variants (preserved verbatim from v3.6 App.tsx).
  const variants = useMemo(
    () => ({
      initial: { opacity: 0, y: 12 },
      animate: {
        opacity: 1,
        y: 0,
        transition: { duration: DURATION.base, ease: EASE_OUT_SOFT },
      },
      exit: {
        opacity: 0,
        y: -8,
        transition: { duration: DURATION.fast, ease: EASE_OUT_SOFT },
      },
    }),
    [],
  );

  return (
    <>
      {oauthError ? (
        <div
          role="alert"
          className="fixed left-1/2 top-4 z-50 max-w-sm -translate-x-1/2 rounded-lg border border-red-300 bg-red-50 px-4 py-2.5 text-[13px] leading-snug text-red-900 shadow-md"
          onClick={() => setOauthError(null)}
        >
          {oauthError}
          <span className="ml-2 cursor-pointer text-red-500" aria-label="Dismiss">
            ×
          </span>
        </div>
      ) : null}
      <AnimatePresence mode="wait">
      {screen === "signin" ? (
        <motion.div key="signin" {...variants}>
          <SignInScreen
            initialEmail={email}
            onSent={(e, opts) => {
              setEmail(e);
              setSignupPassword("");
              setBackfillForced(Boolean(opts?.backfill));
              setWantsReset(false);
              setScreen("otp");
            }}
            onPassword={(e) => {
              setEmail(e);
              setSignupPassword("");
              setBackfillForced(false);
              setWantsReset(false);
              setScreen("password");
            }}
            onCreatePassword={(e) => {
              setEmail(e);
              setSignupPassword("");
              setBackfillForced(false);
              setWantsReset(false);
              setScreen("create-password");
            }}
            // PHONE_LOGIN_ENABLED gates the button inside SignInScreen, so
            // we wire the callback unconditionally; the button never renders
            // when the flag is off, so this is dead code in production until
            // the SMS provider key lands and the flag flips.
            onSwitchToPhone={
              PHONE_LOGIN_ENABLED ? () => setScreen("phone-signin") : undefined
            }
          />
        </motion.div>
      ) : screen === "otp" ? (
        <motion.div key="otp" {...variants}>
          <OtpScreen
            email={email}
            signupPassword={signupPassword || undefined}
            onVerified={({ isNew }) => {
              // BD1: a KNOWN passwordless existing account (check-email told
              // us) must set a password before proceeding — the founder's
              // backfill decree. A "forgot password" round-trip gets the same
              // screen, but skippable. Signup mode (signupPassword) already
              // set the password server-side, so it follows classic routing.
              if (!signupPassword && (backfillForced || wantsReset)) {
                setScreen("set-password");
                return;
              }
              // Match Google-OAuth return routing (see consumeGoogleOAuthReturn
              // above): returning users go straight to /chat with no extra
              // celebratory screen; brand-new accounts get the "success →
              // onboarding" arc so they pick languages/persona before chat.
              if (isNew) {
                setScreen("success");
              } else {
                navigate("/chat", { replace: true });
              }
            }}
            onBack={() => {
              setSignupPassword("");
              setScreen("signin");
            }}
          />
        </motion.div>
      ) : screen === "password" ? (
        <motion.div key="password" {...variants}>
          <PasswordScreen
            email={email}
            onAuthed={() => navigate("/chat", { replace: true })}
            onUseOtp={(reason) => {
              // Fire the code email and move to OTP entry. Fire-and-forget:
              // OtpScreen has its own Resend if this send was swallowed.
              setWantsReset(reason === "forgot");
              void fetch("/api/otp/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
              }).catch(() => {});
              setScreen("otp");
            }}
            onBack={() => setScreen("signin")}
          />
        </motion.div>
      ) : screen === "create-password" ? (
        <motion.div key="create-password" {...variants}>
          <CreatePasswordScreen
            email={email}
            onSent={(pw) => {
              setSignupPassword(pw);
              setScreen("otp");
            }}
            onBack={() => setScreen("signin")}
          />
        </motion.div>
      ) : screen === "set-password" ? (
        <motion.div key="set-password" {...variants}>
          <SetPasswordScreen
            email={email}
            forced={backfillForced}
            onDone={() => navigate("/chat", { replace: true })}
            onSkip={
              backfillForced
                ? undefined
                : () => navigate("/chat", { replace: true })
            }
          />
        </motion.div>
      ) : screen === "phone-signin" ? (
        // Dormant until PHONE_LOGIN_ENABLED. The branch exists in the state
        // machine ahead of time so flipping the flag is a one-line activation
        // — no schema, no new screens to design, just untoggle the gate.
        <motion.div key="phone-signin" {...variants}>
          <PhoneSignInScreen
            initialPhone={phone}
            onSent={(normalizedPhone) => {
              setPhone(normalizedPhone);
              setScreen("phone-otp");
            }}
            onBackToEmail={() => setScreen("signin")}
          />
        </motion.div>
      ) : screen === "phone-otp" ? (
        <motion.div key="phone-otp" {...variants}>
          <PhoneOtpScreen
            phone={phone}
            onVerified={({ isNew }) => {
              // Same isNew → routing decision as email OTP for symmetry.
              if (isNew) {
                setScreen("success");
              } else {
                navigate("/chat", { replace: true });
              }
            }}
            onBack={() => setScreen("phone-signin")}
          />
        </motion.div>
      ) : screen === "success" ? (
        <motion.div key="success" {...variants}>
          <SuccessScreen
            email={email}
            onBack={() => {
              setEmail("");
              setScreen("signin");
            }}
            onContinue={() => setScreen("onboarding")}
          />
        </motion.div>
      ) : screen === "onboarding" ? (
        <motion.div key="onboarding" {...variants}>
          <OnboardingFlow
            onComplete={(finalAnswers) => {
              setAnswers(finalAnswers);
              // TODO: POST /api/user/onboarding/save with finalAnswers.
              // Hand-off to the chat — the chat reads answers back from
              // localStorage (`bsure_onboarding_v1`) so we don't need to
              // pass them through the route.
              setScreen("intro");
            }}
          />
        </motion.div>
      ) : (
        <motion.div key="intro" {...variants}>
          <IntroFlow
            answers={answers ?? DEV_FAKE_ANSWERS}
            onComplete={() => {
              // End of /login flow — route into the chat.
              navigate("/chat");
            }}
            onSkipAll={() => navigate("/chat")}
          />
        </motion.div>
      )}
      </AnimatePresence>
    </>
  );
}

export default LoginRoute;
